/* Article Card Large Slider */

class ArticleCardSlider {
  constructor(element) {
    this.carousel = element;
    this.track = element.querySelector('[data-track]');
    this.slides = Array.from(this.track.querySelectorAll('.article-card-large__slide'));
    this.prevButton = element.querySelector('[data-prev]');
    this.nextButton = element.querySelector('[data-next]');
    this.indicatorsContainer = element.querySelector('[data-indicators]');

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.init();
  }

  init() {
    this.setupAccessibility();
    this.createIndicators();
    this.attachEventListeners();
    this.updateSlider();
    this.positionNavButtons();

    // Reposition buttons on window resize
    window.addEventListener('resize', () => this.positionNavButtons());
  }

  positionNavButtons() {
    const firstSlide = this.slides[0];
    if (!firstSlide) return;

    const imageWrapper = firstSlide.querySelector('.article-card-large__image-wrapper');
    if (!imageWrapper) return;

    const imageRect = imageWrapper.getBoundingClientRect();
    const carouselRect = this.carousel.getBoundingClientRect();

    const imageCenter = imageRect.top - carouselRect.top + imageRect.height / 2;

    if (this.prevButton) {
      this.prevButton.style.top = `${imageCenter}px`;
    }
    if (this.nextButton) {
      this.nextButton.style.top = `${imageCenter}px`;
    }
  }

  setupAccessibility() {
    this.carousel.setAttribute('role', 'region');
    this.carousel.setAttribute('aria-roledescription', 'carousel');
    this.carousel.setAttribute('aria-label', 'Featured articles carousel');

    this.liveRegion = document.createElement('div');
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    this.liveRegion.className = 'visually-hidden';
    this.carousel.appendChild(this.liveRegion);

    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} of ${this.totalSlides}`);
    });
  }

  createIndicators() {
    if (!this.indicatorsContainer) return;

    this.indicatorsContainer.setAttribute('role', 'tablist');
    this.indicatorsContainer.setAttribute('aria-label', 'Slide navigation');

    for (let i = 0; i < this.totalSlides; i++) {
      const indicator = document.createElement('button');
      indicator.type = 'button';
      indicator.className = 'carousel__indicator';
      indicator.setAttribute('role', 'tab');
      indicator.setAttribute('aria-label', `Go to slide ${i + 1} of ${this.totalSlides}`);
      indicator.setAttribute('aria-controls', `slide-${i}`);
      indicator.dataset.index = i;

      if (i === 0) {
        indicator.classList.add('is-active');
        indicator.setAttribute('aria-selected', 'true');
      } else {
        indicator.setAttribute('aria-selected', 'false');
      }

      indicator.addEventListener('click', () => this.goToSlide(i));
      this.indicatorsContainer.appendChild(indicator);
    }

    this.indicators = Array.from(this.indicatorsContainer.querySelectorAll('.carousel__indicator'));
  }

  goToSlide(index) {
    this.currentIndex = index;
    this.updateSlider();
  }

  prev() {
    this.currentIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.updateSlider();
  }

  next() {
    this.currentIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.updateSlider();
  }

  updateSlider() {
    const translateX = -this.currentIndex * 100;
    this.track.style.transform = `translateX(${translateX}%)`;

    this.slides.forEach((slide, index) => {
      if (index === this.currentIndex) {
        slide.removeAttribute('aria-hidden');
        slide.setAttribute('aria-current', 'true');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('aria-current');
      }
    });

    if (this.indicators) {
      this.indicators.forEach((indicator, index) => {
        const isActive = index === this.currentIndex;
        indicator.classList.toggle('is-active', isActive);
        indicator.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    // Announce to screen readers
    if (this.liveRegion) {
      this.liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${this.totalSlides}`;
    }
  }

  attachEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    this.carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });

    this.initSwipeSupport();
  }

  initSwipeSupport() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    });

    this.track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
    });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;

      const diff = startX - currentX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    });
  }
}

// Initialize slider
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-article-carousel]');
  sliders.forEach((slider) => new ArticleCardSlider(slider));
});
